import {
  BookOpen,
  Check,
  ChevronDown,
  ChevronsUpDown,
  ChevronUp,
  Edit2,
  Plus,
  RefreshCw,
  Target,
  Trash2,
  X,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-hot-toast'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { SimpleSelect } from '@/components/ui/simple-select'
import { cn } from '@/lib/utils'
import { useRecipes } from './hooks/use-recipes'
import type { Subtopic, SubtopicFormData, Topic } from './hooks/use-subtopics'
import { useSubtopics } from './hooks/use-subtopics'

interface RecipeOption {
  id: string
  name: string
}

interface SubtopicModalProps {
  isOpen: boolean
  subtopic?: Subtopic | null
  topics: Topic[]
  onClose: () => void
  onSubmit: (data: SubtopicFormData) => Promise<void>
  isSubmitting: boolean
  allRecipes?: RecipeOption[]
}

function SubtopicModal({
  isOpen,
  subtopic,
  topics,
  onClose,
  onSubmit,
  isSubmitting,
  allRecipes = [],
}: SubtopicModalProps) {
  const isEditMode = !!subtopic
  const [formData, setFormData] = useState<SubtopicFormData>({
    topicId: subtopic?.topicId || '',
    name: subtopic?.name || '',
    description: subtopic?.description || '',
  })
  const [selectedRecipeIds, setSelectedRecipeIds] = useState<string[]>([])
  const [recipeDropdownOpen, setRecipeDropdownOpen] = useState(false)

  // Sync form when modal opens or subtopic changes (Add vs Edit / different subtopic)
  useEffect(() => {
    if (!isOpen) return
    setFormData({
      topicId: subtopic?.topicId || '',
      name: subtopic?.name || '',
      description: subtopic?.description || '',
    })
    setSelectedRecipeIds(subtopic?.recipes?.map((r) => r.id) || [])
  }, [isOpen, subtopic])

  // Toggle recipe selection
  const toggleRecipe = (recipeId: string) => {
    setSelectedRecipeIds((prev) =>
      prev.includes(recipeId)
        ? prev.filter((id) => id !== recipeId)
        : [...prev, recipeId],
    )
  }

  const handleSubmit = () => {
    onSubmit({
      ...formData,
      recipeIds: selectedRecipeIds,
    })
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-card border-border w-full max-w-md rounded-lg border p-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold">
            {isEditMode ? 'Edit Subtopic' : 'Create Subtopic'}
          </h3>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="space-y-4">
          {/* Topic: required select on create, fixed on edit (no topic moves) */}
          <div>
            <label className="text-sm font-medium">
              Topic {!isEditMode && <span className="text-destructive">*</span>}
            </label>
            {isEditMode ? (
              <p className="text-muted-foreground mt-1 text-sm">
                {subtopic?.topicName}
              </p>
            ) : (
              <SimpleSelect
                value={formData.topicId}
                onChange={(topicId) =>
                  setFormData((prev) => ({ ...prev, topicId }))
                }
                options={topics.map((t) => ({ value: t.id, label: t.name }))}
                placeholder="Select a topic..."
                width="full"
              />
            )}
          </div>

          <div>
            <label className="text-sm font-medium">Name</label>
            <Input
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
              placeholder="e.g. Two Pointers, Binary Search"
            />
          </div>

          <div>
            <label className="text-sm font-medium">Description</label>
            <textarea
              className="border-input bg-background w-full rounded-md border px-3 py-2 text-sm"
              rows={3}
              value={formData.description || ''}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              placeholder="Optional description..."
            />
          </div>

          {/* Linked Recipes */}
          {allRecipes.length > 0 && (
            <div>
              <label className="mb-1 block text-sm font-medium">
                Linked Recipes
              </label>
              <Popover
                open={recipeDropdownOpen}
                onOpenChange={setRecipeDropdownOpen}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={recipeDropdownOpen}
                    className="w-full justify-between"
                  >
                    {selectedRecipeIds.length > 0
                      ? `${selectedRecipeIds.length} recipe${selectedRecipeIds.length === 1 ? '' : 's'} selected`
                      : 'Select recipes...'}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search recipes..." />
                    <CommandList>
                      <CommandEmpty>No recipes found.</CommandEmpty>
                      <CommandGroup>
                        {allRecipes.map((rec) => (
                          <CommandItem
                            key={rec.id}
                            value={rec.name}
                            onSelect={() => toggleRecipe(rec.id)}
                          >
                            <Check
                              className={cn(
                                'mr-2 h-4 w-4',
                                selectedRecipeIds.includes(rec.id)
                                  ? 'opacity-100'
                                  : 'opacity-0',
                              )}
                            />
                            {rec.name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {/* Show selected recipes as badges */}
              {selectedRecipeIds.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {selectedRecipeIds.map((id) => {
                    const rec = allRecipes.find((r) => r.id === id)
                    if (!rec) return null
                    return (
                      <span
                        key={id}
                        className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-1 text-xs text-amber-600 dark:text-amber-400"
                      >
                        {rec.name}
                        <button
                          type="button"
                          onClick={() => toggleRecipe(id)}
                          className="hover:text-amber-800 dark:hover:text-amber-200"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    )
                  })}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={
                isSubmitting ||
                !formData.name.trim() ||
                (!isEditMode && !formData.topicId)
              }
            >
              {isSubmitting ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export function TaxonomySection() {
  const {
    subtopics,
    topics,
    loading,
    error,
    refresh,
    createSubtopic,
    updateSubtopic,
    deleteSubtopic,
  } = useSubtopics()

  const { recipes } = useRecipes()

  const [isCollapsed, setIsCollapsed] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [editingSubtopic, setEditingSubtopic] = useState<Subtopic | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Group filtered subtopics under their topics, preserving topic displayOrder
  // (topics arrive ordered by displayOrder from the API).
  const groupedSubtopics = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    const filtered = subtopics.filter(
      (s) => !query || s.name.toLowerCase().includes(query),
    )
    const byTopicId = new Map<string, Subtopic[]>()
    for (const subtopic of filtered) {
      if (!byTopicId.has(subtopic.topicId)) {
        byTopicId.set(subtopic.topicId, [])
      }
      byTopicId.get(subtopic.topicId)!.push(subtopic)
    }
    return topics
      .filter((t) => byTopicId.has(t.id))
      .map((t) => ({ topic: t, subtopics: byTopicId.get(t.id)! }))
  }, [subtopics, topics, searchQuery])

  const handleCreate = () => {
    setEditingSubtopic(null)
    setModalOpen(true)
  }

  const handleEdit = (subtopic: Subtopic) => {
    setEditingSubtopic(subtopic)
    setModalOpen(true)
  }

  const handleSubmit = async (data: SubtopicFormData) => {
    setIsSubmitting(true)
    try {
      if (editingSubtopic) {
        // Topic moves are not supported — only send mutable fields
        await updateSubtopic(editingSubtopic.id, {
          name: data.name,
          description: data.description,
          recipeIds: data.recipeIds,
        })
        toast.success('Subtopic updated')
      } else {
        await createSubtopic(data)
        toast.success('Subtopic created')
      }
      setModalOpen(false)
      setEditingSubtopic(null)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async (subtopic: Subtopic) => {
    if (!confirm(`Delete "${subtopic.name}"?`)) return
    try {
      await deleteSubtopic(subtopic.id)
      toast.success('Subtopic deleted')
    } catch (err) {
      // Surfaces the router's guardrail message verbatim (e.g. "Cannot
      // delete: N approach links, N problem tags reference this subtopic")
      toast.error(err instanceof Error ? err.message : 'Failed to delete')
    }
  }

  return (
    <div className="mt-8 rounded-lg border border-border bg-card">
      {/* Collapsible Header */}
      <div className="flex items-center justify-between p-4">
        <button
          type="button"
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="-m-2 flex flex-1 items-center gap-3 rounded-lg p-2 transition-colors hover:bg-muted/50"
        >
          <div className="rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 p-2">
            <Target size={20} className="text-white" />
          </div>
          <div className="text-left">
            <h2 className="text-lg font-semibold text-foreground">Taxonomy</h2>
            <p className="text-sm text-muted-foreground">
              {subtopics.length} subtopic{subtopics.length !== 1 ? 's' : ''}{' '}
              across {topics.length} topic{topics.length !== 1 ? 's' : ''}
            </p>
          </div>
          {isCollapsed ? (
            <ChevronDown size={20} className="text-muted-foreground" />
          ) : (
            <ChevronUp size={20} className="text-muted-foreground" />
          )}
        </button>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={handleCreate}>
            <Plus size={16} className="mr-1" />
            Add Subtopic
          </Button>
          <Button variant="outline" size="sm" onClick={refresh}>
            <RefreshCw size={16} />
          </Button>
        </div>
      </div>

      {/* Content - Collapsible */}
      {!isCollapsed && (
        <div className="border-t border-border p-4">
          {error ? (
            <div className="py-8 text-center">
              <p className="mb-2 text-muted-foreground">{error}</p>
              <Button variant="outline" size="sm" onClick={refresh}>
                Retry
              </Button>
            </div>
          ) : loading ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              Loading subtopics...
            </div>
          ) : subtopics.length === 0 ? (
            <div className="py-8 text-center">
              <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                <Target size={24} className="text-muted-foreground" />
              </div>
              <h3 className="mb-2 font-medium text-foreground">
                No subtopics yet
              </h3>
              <p className="mb-4 text-sm text-muted-foreground">
                Create subtopics to categorize problem-solving approaches.
              </p>
              <Button size="sm" onClick={handleCreate}>
                <Plus size={16} className="mr-1" />
                Create Subtopic
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {/* Search */}
              <div className="mb-4">
                <Input
                  placeholder="Search subtopics..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full"
                />
              </div>

              {/* Subtopic list grouped by topic */}
              {groupedSubtopics.length === 0 ? (
                <div className="py-8 text-center">
                  <p className="text-muted-foreground">
                    No subtopics match your search
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {groupedSubtopics.map(({ topic, subtopics: items }) => (
                    <div
                      key={topic.id}
                      className="rounded-lg border border-border"
                    >
                      <div className="border-b border-border bg-muted/30 px-4 py-2">
                        <h3 className="text-sm font-semibold text-foreground">
                          {topic.name}
                        </h3>
                      </div>
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="px-4 py-2 text-left text-sm font-medium text-muted-foreground">
                              Name
                            </th>
                            <th className="px-4 py-2 text-left text-sm font-medium text-muted-foreground">
                              Description
                            </th>
                            <th className="w-24 px-4 py-2 text-right text-sm font-medium text-muted-foreground">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((subtopic) => {
                            const linkedRecipes = subtopic.recipes || []
                            return (
                              <tr
                                key={subtopic.id}
                                className="border-b border-border last:border-0 hover:bg-muted/50"
                              >
                                <td className="px-4 py-2 text-sm font-medium">
                                  <div className="flex items-center gap-2">
                                    {subtopic.name}
                                    {linkedRecipes.length > 0 && (
                                      <span
                                        className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-xs text-amber-600 dark:text-amber-400"
                                        title={`Linked to: ${linkedRecipes.map((r) => r.name).join(', ')}`}
                                      >
                                        <BookOpen className="h-3 w-3" />
                                        {linkedRecipes.length}
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-2 text-sm text-muted-foreground">
                                  {subtopic.description || '—'}
                                </td>
                                <td className="px-4 py-2 text-right">
                                  <div className="flex justify-end gap-1">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleEdit(subtopic)}
                                    >
                                      <Edit2 className="h-3 w-3" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleDelete(subtopic)}
                                      className="text-destructive hover:text-destructive"
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <SubtopicModal
        isOpen={modalOpen}
        subtopic={editingSubtopic}
        topics={topics}
        onClose={() => {
          setModalOpen(false)
          setEditingSubtopic(null)
        }}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        allRecipes={recipes.map((r) => ({
          id: r.id,
          name: r.name,
        }))}
      />
    </div>
  )
}
